import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth, hasRole } from "@/lib/auth";
import PanelState, { extractApiError } from "@/components/PanelState";
import { Mail, CheckCircle2, XCircle, Send, Loader2, AlertTriangle, Search as SearchIcon, RefreshCw, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

// ============================================================================
// Thank You Cards — CSR-facing queue + approval page (2026-08-11).
// See PRD 2026-08-11 ADR. Auto-send toggle is per-tenant; only owners/admins
// can flip it. All CSRs can select/approve/unapprove/cancel/view (Cancel
// widened 2026-08-12 to match Approve — CSR manages their own queue).
// Backfill batch_tags never auto-approve regardless of toggle.
// ============================================================================

// Cancel-reason taxonomy (2026-08-12). Codes are backend-normalized so
// future reporting can group by reason without regex over freeform text.
// Order = what CSR sees top-to-bottom in the picker. "Other" is always last
// and reveals a freeform textarea (required in that case).
const CANCEL_REASONS = [
  { code: "customer_opted_out", label: "Customer opted out",  hint: "Explicitly declined the card" },
  { code: "wrong_address",       label: "Wrong address",       hint: "Address is bad or unmailable" },
  { code: "duplicate_job",       label: "Duplicate job",       hint: "Same job already has a card in queue" },
  { code: "callback_pending",    label: "Callback pending",    hint: "Waiting on customer follow-up before sending" },
  { code: "other",               label: "Other",               hint: "Freeform — required" },
];
const CANCEL_REASON_LABEL = CANCEL_REASONS.reduce((m, r) => { m[r.code] = r.label; return m; }, {});

const STATUSES = [
  { key: "QUEUED",    label: "Queued",     tone: "bg-[#fef3c7] text-[#854d0e] border-[#facc15]" },
  { key: "APPROVED",  label: "Approved",   tone: "bg-[#e7f4fb] text-[#0072bc] border-[#7fc6ea]" },
  { key: "SENT",      label: "Sent",       tone: "bg-[#dcfce7] text-[#166534] border-[#4ade80]" },
  { key: "FAILED",    label: "Failed",     tone: "bg-[#fee2e2] text-[#991b1b] border-[#f87171]" },
  { key: "CANCELLED", label: "Cancelled",  tone: "bg-[#e5e7eb] text-[#4b5563] border-[#9ca3af]" },
];

// 2026-08-12: virtual "Commercial $1K+" tab. Not a status — this filter
// slices across QUEUED/APPROVED for cards that are permanently gated
// from auto-send (job_type COMMERCIAL + revenue > $1K). Keys must not
// collide with real statuses; using COMMERCIAL_LOCKED for both the tab
// key and the counts field the backend returns.
const COMMERCIAL_LOCKED_TAB = {
  key: "COMMERCIAL_LOCKED",
  label: "Commercial $1K+",
  tone: "bg-[#fde8e8] text-[#991b1b] border-[#f87171]",
};

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function fmtAddress(a) {
  if (!a) return "—";
  const l1 = a.line1 || "";
  const l2 = a.line2 ? ` ${a.line2}` : "";
  const cityStateZip = [a.city, a.state, a.postal_code].filter(Boolean).join(" ");
  const parts = [];
  if (l1 || l2) parts.push(`${l1}${l2}`.trim());
  if (cityStateZip) parts.push(cityStateZip);
  return parts.length ? parts.join(" · ") : "—";
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function StatusPill({ status }) {
  const s = STATUSES.find((x) => x.key === status);
  if (!s) return <span className="text-[10.5px] font-mono-data">{status}</span>;
  return (
    <span data-testid={`tyc-status-pill-${status}`} className={`inline-flex text-[10px] font-mono-data uppercase tracking-widest border rounded px-2 py-0.5 ${s.tone}`}>
      {s.label}
    </span>
  );
}

function BatchBadge({ tag }) {
  const isBackfill = tag && tag !== "daily";
  const cls = isBackfill
    ? "bg-[#fef3c7] text-[#854d0e] border-[#facc15]"
    : "bg-white text-[color:var(--cc-ink-muted)] border-[#e7e9ee]";
  return (
    <span className={`inline-flex text-[9.5px] font-mono-data uppercase tracking-widest border rounded px-1.5 py-0.5 ${cls}`}>
      {tag || "daily"}
    </span>
  );
}

// Clickable table-header cell that toggles the list sort. First click on an
// inactive column sorts DESC (newest first — matches reviewer expectation
// on a date column); second click flips to ASC; third click cycles back to
// DESC. Keeps the sort contract narrow (no "unsorted" state) so keyset
// pagination on the server stays coherent.
function SortableTh({ label, field, sort, onSort, testId, align = "left" }) {
  const active = sort.startsWith(`${field}_`);
  const dir = active ? (sort.endsWith("_desc") ? "desc" : "asc") : null;
  const toggle = () => {
    if (!active) onSort(`${field}_desc`);
    else if (dir === "desc") onSort(`${field}_asc`);
    else onSort(`${field}_desc`);
  };
  const Icon = !active ? ArrowUpDown : dir === "desc" ? ArrowDown : ArrowUp;
  return (
    <th className={`text-${align} px-3 py-2.5`}>
      <button
        type="button"
        onClick={toggle}
        data-testid={testId}
        data-sort-state={active ? dir : "inactive"}
        className={`inline-flex items-center gap-1 uppercase tracking-widest text-[10px] font-semibold ${
          active ? "text-[#0072bc]" : "text-[color:var(--cc-ink-muted)] hover:text-[#0e1420]"
        }`}
      >
        {label}
        <Icon size={11} strokeWidth={2.2} />
      </button>
    </th>
  );
}

export default function ThankYouCards() {
  const { user } = useAuth();
  // 2026-08-12: use `hasRole()` which normalizes legacy role names
  // (dispatch_csr→call_center, gm_ops→administrative). Previously a raw
  // `.includes(user.role)` here silently rejected Juliana on prod because
  // her seed row still had the pre-2026-Q1 `dispatch_csr` string.
  const canManageSettings = hasRole(user, "platform_admin", "owner", "administrative");
  // Cancel matches Approve's role set exactly. CSR manages their own queue
  // and needs to decline cards, not just approve them. Settings/backfill
  // toggle intentionally stays owner/admin only.
  const canCancel = hasRole(user, "platform_admin", "owner", "administrative", "call_center");
  const [statusFilter, setStatusFilter] = useState("QUEUED");
  const [batchFilter, setBatchFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // Sort state — reviewer can flip between when the job was completed vs
  // when the card landed in our queue, and each has an asc/desc toggle.
  // Default = created_at_desc to match pre-2026-08-11 behaviour.
  const [sort, setSort] = useState("created_at_desc");
  const [data, setData] = useState(null);
  const [loadState, setLoadState] = useState({ status: "loading", error: null });
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null); // {action, acted, skipped, elapsedMs}
  const [detailCard, setDetailCard] = useState(null);
  const [settingBusy, setSettingBusy] = useState(false);
  // Cancel dialog state (2026-08-12). Kept separate from `bulkBusy` so the
  // picker can validate the reason locally before we spin up the button.
  const [cancelDialog, setCancelDialog] = useState(null); // { ids: string[] } | null
  const [cancelCode, setCancelCode] = useState(null);
  const [cancelOtherText, setCancelOtherText] = useState("");

  // Debounce search — keeps typing responsive without hammering the backend.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = async (opts = {}) => {
    const silent = opts.silent === true;
    if (!silent) setLoadState({ status: "loading", error: null });
    try {
      // Commercial-locked tab is a virtual slice — pass through as its own
      // query flag rather than a real status. Backend keeps `status`
      // unfiltered (QUEUED + APPROVED both surface) while narrowing to
      // job_type COMMERCIAL + revenue > $1000.
      const isCommercialTab = statusFilter === "COMMERCIAL_LOCKED";
      const params = {
        status: isCommercialTab ? "ALL" : statusFilter,
        limit: 500,
        sort,
      };
      if (isCommercialTab) params.commercial_locked = true;
      if (batchFilter !== "ALL") params.batch_tag = batchFilter;
      if (debouncedSearch) params.search = debouncedSearch;
      const { data: resp } = await api.get("/thank-you-cards", { params });
      setData(resp);
      if (!silent) setLoadState({ status: "ok", error: null });
    } catch (err) {
      if (!silent) {
        setData(null);
        setLoadState({ status: "error", error: extractApiError(err) });
      }
    }
  };

  useEffect(() => { load(); }, [statusFilter, batchFilter, debouncedSearch, sort]);

  // ---------------------------------------------------------------
  // Passive auto-refresh (2026-08-11) — Agent 12 ingests on a 2h cycle;
  // reviewers who leave the page open shouldn't have to remember to hit
  // refresh to see new cards land. Rules:
  //   * 75s cadence (mid-range of what we agreed on).
  //   * Skip while the tab is backgrounded (no burning cycles on hidden
  //     tabs, and the visibilitychange listener does an immediate refetch
  //     the moment the tab returns to foreground).
  //   * Skip while a bulk action is in flight or a modal is open — the
  //     silent load would race with a bulk PATCH and could flicker rows
  //     out from under the reviewer's cursor.
  //   * Silent load — no loading flash, no scroll jump.
  // Backend auto-send + poller are unaffected; this is UX-only.
  // ---------------------------------------------------------------
  useEffect(() => {
    const AUTO_REFRESH_MS = 75_000;
    const shouldSkip = () =>
      bulkBusy ||
      detailCard != null ||
      bulkResult != null ||
      document.visibilityState !== "visible";

    const tick = () => { if (!shouldSkip()) load({ silent: true }); };
    const id = setInterval(tick, AUTO_REFRESH_MS);
    // Immediate refresh whenever the tab comes back to foreground so the
    // reviewer sees the latest state the moment they look at it.
    const onVisibility = () => {
      if (document.visibilityState === "visible" && !bulkBusy && !detailCard && !bulkResult) {
        load({ silent: true });
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [statusFilter, batchFilter, debouncedSearch, sort, bulkBusy, detailCard, bulkResult]);

  // Clear selection whenever the visible list changes to avoid ghost selections.
  useEffect(() => { setSelected(new Set()); }, [statusFilter, batchFilter, debouncedSearch, sort]);

  const cards = data?.cards || [];
  const counts = data?.counts || { QUEUED: 0, APPROVED: 0, SENT: 0, FAILED: 0, CANCELLED: 0 };
  const settings = data?.settings || { auto_send_enabled: false };
  const batchTags = data?.batch_tags || [];
  const pendingBackfillTags = data?.pending_backfill_tags || [];

  const selectableCards = useMemo(
    // 2026-08-21: FAILED added — Brett needs to cancel duplicate CC records
    // that landed in FAILED. Backend already allows cancel from any status
    // except SENT / CANCELLED (see server.py cancel_thank_you_cards
    // `$nin: [SENT, CANCELLED]`), so the UI was the only blocker. Approve
    // and Unapprove buttons stay gated on selectedQueued/selectedApproved
    // so a FAILED selection only surfaces the Cancel action.
    () => cards.filter((c) => c.status === "QUEUED" || c.status === "APPROVED" || c.status === "FAILED"),
    [cards],
  );
  const allSelected = selectableCards.length > 0 && selectableCards.every((c) => selected.has(c.id));

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(selectableCards.map((c) => c.id)));
  };

  const selectedArray = [...selected];
  const selectedCards = cards.filter((c) => selected.has(c.id));
  const selectedQueued = selectedCards.filter((c) => c.status === "QUEUED");
  const selectedApproved = selectedCards.filter((c) => c.status === "APPROVED");

  const doApprove = async (ids) => {
    if (ids.length === 0) return;
    setBulkBusy(true);
    const t0 = performance.now();
    try {
      const { data: resp } = await api.post("/thank-you-cards/approve", { card_ids: ids });
      const elapsedMs = Math.round(performance.now() - t0);
      setBulkResult({
        action: "Approve",
        requested: ids.length,
        acted: resp.approved || [],
        skipped: resp.skipped || [],
        elapsedMs,
      });
      setSelected(new Set());
      await load({ silent: true });
    } catch (err) {
      setBulkResult({
        action: "Approve",
        requested: ids.length,
        acted: [],
        skipped: [],
        elapsedMs: Math.round(performance.now() - t0),
        error: extractApiError(err)?.message || "Approve failed",
      });
    } finally {
      setBulkBusy(false);
    }
  };

  const doUnapprove = async (ids) => {
    if (ids.length === 0) return;
    setBulkBusy(true);
    const t0 = performance.now();
    try {
      const { data: resp } = await api.post("/thank-you-cards/unapprove", { card_ids: ids });
      setBulkResult({
        action: "Unapprove",
        requested: ids.length,
        acted: resp.unapproved || [],
        skipped: resp.skipped || [],
        elapsedMs: Math.round(performance.now() - t0),
      });
      setSelected(new Set());
      await load({ silent: true });
    } catch (err) {
      setBulkResult({
        action: "Unapprove",
        requested: ids.length,
        acted: [],
        skipped: [],
        elapsedMs: Math.round(performance.now() - t0),
        error: extractApiError(err)?.message || "Unapprove failed",
      });
    } finally {
      setBulkBusy(false);
    }
  };

  const doCancel = (ids) => {
    if (ids.length === 0 || !canCancel) return;
    // 2026-08-12: replaces the old window.prompt with a taxonomy-driven
    // dialog. State reset happens here so a re-open starts clean.
    setCancelCode(null);
    setCancelOtherText("");
    setCancelDialog({ ids });
  };

  const submitCancel = async () => {
    if (!cancelDialog) return;
    const ids = cancelDialog.ids;
    const code = cancelCode;
    if (!code) return; // guard — the Confirm button is disabled anyway
    // For "other" the freeform text is the payload reason. For canned codes
    // the label serves as the human-readable reason so audit-trail display
    // reads naturally without a lookup.
    const isOther = code === "other";
    const reason = isOther ? cancelOtherText.trim() : CANCEL_REASON_LABEL[code];
    if (isOther && !reason) return;
    setCancelDialog(null);
    setBulkBusy(true);
    const t0 = performance.now();
    try {
      const { data: resp } = await api.post("/thank-you-cards/cancel", {
        card_ids: ids,
        reason,
        reason_code: code,
      });
      setBulkResult({
        action: "Cancel",
        requested: ids.length,
        acted: resp.cancelled || [],
        skipped: resp.skipped || [],
        elapsedMs: Math.round(performance.now() - t0),
      });
      setSelected(new Set());
      await load({ silent: true });
    } catch (err) {
      setBulkResult({
        action: "Cancel",
        requested: ids.length,
        acted: [],
        skipped: [],
        elapsedMs: Math.round(performance.now() - t0),
        error: extractApiError(err)?.message || "Cancel failed",
      });
    } finally {
      setBulkBusy(false);
    }
  };

  const toggleAutoSend = async () => {
    if (!canManageSettings) return;
    setSettingBusy(true);
    try {
      await api.patch("/thank-you-cards/settings", { auto_send_enabled: !settings.auto_send_enabled });
      await load({ silent: true });
    } catch (err) {
      alert(extractApiError(err)?.message || "Setting update failed");
    } finally {
      setSettingBusy(false);
    }
  };

  const isEmpty = loadState.status === "ok" && cards.length === 0;

  return (
    <div className="space-y-5" data-testid="thank-you-cards-page">
      {/* Header */}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[.15em] text-[#0072bc] font-bold">
            <Mail size={14} strokeWidth={2} />
            <span>Handwrytten queue</span>
          </div>
          <h1 className="font-display font-bold text-[26px] sm:text-[32px] tracking-tight text-[#0e1420] mt-1">
            Thank You Cards
          </h1>
          <p className="text-[13.5px] text-[color:var(--cc-ink-muted)] mt-1 max-w-[640px]">
            VPS drops queued cards here — CSRs approve, and a background poller fires the actual Handwrytten send.
            Auto-send off by default; backfill batches always require approval.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <button
            type="button"
            onClick={() => load()}
            data-testid="tyc-refresh"
            className="p-2 rounded-lg border border-[#e7e9ee] bg-white text-[#0e1420] hover:bg-[#f5f6f8]"
            title="Refresh"
          >
            <RefreshCw size={14} strokeWidth={1.8} />
          </button>
          <AutoSendToggle
            enabled={settings.auto_send_enabled}
            onToggle={toggleAutoSend}
            disabled={!canManageSettings || settingBusy}
            updatedAt={settings.updated_at}
            updatedBy={settings.updated_by}
          />
        </div>
      </header>

      {/* Backfill guardrail explainer — only while at least one non-daily
          batch has cards still QUEUED or APPROVED. Retracts once every
          backfill card resolves to SENT/CANCELLED (fixed 2026-08-11
          iteration 35 cosmetic flag). */}
      {pendingBackfillTags.length > 0 && (
        <div className="bg-[#fef3c7] border border-[#facc15] rounded-xl p-4 flex items-start gap-3" data-testid="tyc-backfill-notice">
          <AlertTriangle size={18} strokeWidth={1.8} className="text-[#854d0e] mt-0.5 shrink-0" />
          <div className="text-[13px] text-[#854d0e]">
            <b>Backfill / campaign batches always require approval</b> — auto-send only applies to <span className="font-mono-data">daily</span> cards.
            Use the batch filter above the table to isolate them.
          </div>
        </div>
      )}

      {/* Status tabs */}
      <div className="flex flex-wrap gap-2" data-testid="tyc-status-tabs">
        {STATUSES.map((s) => {
          const active = statusFilter === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => setStatusFilter(s.key)}
              data-testid={`tyc-tab-${s.key}`}
              className={`inline-flex items-center gap-2 text-[12px] font-semibold px-3.5 py-2 rounded-lg border transition ${
                active
                  ? "bg-[#0e1420] text-white border-[#0e1420]"
                  : "bg-white text-[#0e1420] border-[#e7e9ee] hover:bg-[#f5f6f8]"
              }`}
            >
              <span>{s.label}</span>
              <span className={`min-w-[22px] text-center px-1.5 py-0.5 rounded-md text-[10.5px] font-mono-data ${
                active ? "bg-white/15 text-white" : "bg-[#f5f6f8] text-[color:var(--cc-ink-muted)]"
              }`}>
                {counts[s.key] ?? 0}
              </span>
            </button>
          );
        })}
        {/* Virtual "Commercial $1K+" tab (2026-08-12). Distinct visual weight
            so CSRs know this is a permanently-gated class, not just another
            status filter. Sits at the end of the row on purpose — Queued is
            still the primary daily-work landing tab. */}
        {(() => {
          const s = COMMERCIAL_LOCKED_TAB;
          const active = statusFilter === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => setStatusFilter(s.key)}
              data-testid={`tyc-tab-${s.key}`}
              className={`inline-flex items-center gap-2 text-[12px] font-semibold px-3.5 py-2 rounded-lg border-2 transition ${
                active
                  ? "bg-[#991b1b] text-white border-[#991b1b]"
                  : "bg-white text-[#991b1b] border-[#f87171] hover:bg-[#fef2f2]"
              }`}
            >
              <span>{s.label}</span>
              <span className={`min-w-[22px] text-center px-1.5 py-0.5 rounded-md text-[10.5px] font-mono-data ${
                active ? "bg-white/20 text-white" : "bg-[#fee2e2] text-[#991b1b]"
              }`}>
                {counts.COMMERCIAL_LOCKED ?? 0}
              </span>
            </button>
          );
        })()}
      </div>

      {/* Commercial-lock banner — only visible on that tab. Fixed language:
          this is a permanent rule, not a tenant setting. */}
      {statusFilter === "COMMERCIAL_LOCKED" && (
        <div
          className="bg-[#fee2e2] border-2 border-[#f87171] rounded-xl p-4 flex items-start gap-3"
          data-testid="tyc-commercial-lock-banner"
        >
          <AlertTriangle size={18} strokeWidth={1.8} className="text-[#991b1b] mt-0.5 shrink-0" />
          <div className="text-[13px] text-[#991b1b]">
            <b>These cards will NEVER auto-send even with the toggle on.</b>{" "}
            Commercial jobs over $1,000 require manual approval — this is a permanent
            business rule, not a per-tenant setting. Approve or Cancel each card individually.
          </div>
        </div>
      )}

      {/* Filters row */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <SearchIcon size={13} strokeWidth={1.8} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--cc-ink-muted)]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="tyc-search"
            placeholder="Search customer / job # / tech…"
            className="pl-8 pr-3 py-2 w-72 text-[13px] border border-[#e7e9ee] rounded-lg bg-white outline-none focus:border-[#0072bc]"
          />
        </div>
        <select
          value={batchFilter}
          onChange={(e) => setBatchFilter(e.target.value)}
          data-testid="tyc-batch-filter"
          className="px-3 py-2 text-[13px] border border-[#e7e9ee] rounded-lg bg-white outline-none"
        >
          <option value="ALL">All batches</option>
          {batchTags.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <div className="flex-1" />
        {selected.size > 0 && (
          <div className="flex items-center gap-2" data-testid="tyc-bulk-actions">
            <span className="text-[12px] font-semibold text-[#0e1420]">{selected.size} selected</span>
            {selectedQueued.length > 0 && (
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => doApprove(selectedQueued.map((c) => c.id))}
                data-testid="tyc-bulk-approve"
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-2 bg-[#0072bc] text-white rounded-lg hover:bg-[#005a95] disabled:opacity-60"
              >
                <CheckCircle2 size={14} strokeWidth={2} />
                Approve {selectedQueued.length}
              </button>
            )}
            {selectedApproved.length > 0 && (
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => doUnapprove(selectedApproved.map((c) => c.id))}
                data-testid="tyc-bulk-unapprove"
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-2 border border-[#e7e9ee] bg-white text-[#0e1420] rounded-lg hover:bg-[#f5f6f8] disabled:opacity-60"
              >
                Unapprove {selectedApproved.length}
              </button>
            )}
            {canCancel && (
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => doCancel(selectedArray)}
                data-testid="tyc-bulk-cancel"
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-2 border border-[#f87171] text-[#991b1b] bg-white rounded-lg hover:bg-[#fee2e2] disabled:opacity-60"
              >
                <XCircle size={14} strokeWidth={2} />
                Cancel
              </button>
            )}
          </div>
        )}
      </div>

      {/* Card table */}
      {loadState.status !== "ok" || isEmpty ? (
        <PanelState
          status={loadState.status}
          error={loadState.error}
          isEmpty={isEmpty}
          onRetry={() => load()}
          emptyTitle={
            statusFilter === "QUEUED" ? "Nothing to approve"
            : statusFilter === "COMMERCIAL_LOCKED" ? "No commercial $1K+ cards in queue"
            : `No ${STATUSES.find(s => s.key === statusFilter)?.label.toLowerCase() || ""} cards`
          }
          emptyBody={<>Try switching the status tab, or wait for VPS to drop new cards here.</>}
          testIdPrefix="thank-you-cards"
        />
      ) : (
        <div className="bg-white border border-[#e7e9ee] rounded-xl overflow-hidden" data-testid="tyc-card-table">
          <table className="w-full text-[13px]">
            <thead className="text-[10px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] border-b border-[#eef1f6]">
              <tr>
                <th className="text-left px-3 py-2.5 w-[36px]">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={selectableCards.length === 0}
                    data-testid="tyc-select-all"
                    className="accent-[#0072bc]"
                  />
                </th>
                <th className="text-left px-3 py-2.5">Customer</th>
                <th className="text-left px-3 py-2.5">Job / Tech</th>
                <th className="text-right px-3 py-2.5">Revenue</th>
                <SortableTh
                  label="Completed"
                  field="job_completed_at"
                  sort={sort}
                  onSort={setSort}
                  testId="tyc-sort-completed"
                />
                <th className="text-left px-3 py-2.5">Address</th>
                <th className="text-left px-3 py-2.5">Batch</th>
                <th className="text-left px-3 py-2.5">Status</th>
                <th className="text-right px-3 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {cards.map((c) => {
                const isSelectable = c.status === "QUEUED" || c.status === "APPROVED" || c.status === "FAILED";
                const isSelected = selected.has(c.id);
                return (
                  <tr
                    key={c.id}
                    data-testid={`tyc-row-${c.external_id}`}
                    className={`border-b border-[#eef1f6] last:border-b-0 hover:bg-[#f9fafb] ${isSelected ? "bg-[#e7f4fb]" : ""}`}
                  >
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleOne(c.id)}
                        disabled={!isSelectable}
                        data-testid={`tyc-select-${c.external_id}`}
                        className="accent-[#0072bc]"
                      />
                    </td>
                    <td className="px-3 py-3">
                      <div className="text-[13px] font-semibold text-[#0e1420]">{c.customer_name}</div>
                      <div className="text-[10.5px] font-mono-data text-[color:var(--cc-ink-muted)]">
                        {c.external_id}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="text-[12.5px] text-[#0e1420]">{c.job_number}</div>
                      <div className="text-[11px] text-[color:var(--cc-ink-muted)]">{c.tech_name}</div>
                    </td>
                    <td className="px-3 py-3 text-right font-mono-data font-bold text-[#0072bc]">
                      {fmt$(c.job_revenue)}
                    </td>
                    <td className="px-3 py-3 text-[12px] font-mono-data text-[#0e1420] whitespace-nowrap" data-testid={`tyc-completed-${c.external_id}`}>
                      {fmtDate(c.job_completed_at)}
                    </td>
                    <td className="px-3 py-3 text-[11.5px] text-[color:var(--cc-ink-muted)] max-w-[240px] truncate" title={fmtAddress(c.customer_address)}>
                      {fmtAddress(c.customer_address)}
                    </td>
                    <td className="px-3 py-3">
                      <BatchBadge tag={c.batch_tag} />
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-col gap-1 items-start">
                        <StatusPill status={c.status} />
                        {c.auto_approved && <span className="text-[9.5px] font-mono-data uppercase tracking-widest text-[color:var(--cc-ink-muted)]">auto-approved</span>}
                        {c.status === "SENT" && c.test_mode && (
                          <span data-testid={`tyc-testmode-${c.external_id}`} className="text-[9.5px] font-mono-data uppercase tracking-widest text-[#854d0e]">test mode</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => setDetailCard(c)}
                        data-testid={`tyc-preview-${c.external_id}`}
                        className="text-[11.5px] font-semibold px-2 py-1 rounded border border-[#e7e9ee] hover:bg-[#f5f6f8]"
                      >
                        Preview
                      </button>
                      {c.status === "QUEUED" && (
                        <button
                          type="button"
                          onClick={() => doApprove([c.id])}
                          data-testid={`tyc-approve-${c.external_id}`}
                          className="ml-1.5 text-[11.5px] font-semibold px-2 py-1 rounded bg-[#0072bc] text-white hover:bg-[#005a95]"
                        >
                          Approve
                        </button>
                      )}
                      {c.status === "APPROVED" && (
                        <button
                          type="button"
                          onClick={() => doUnapprove([c.id])}
                          data-testid={`tyc-unapprove-${c.external_id}`}
                          className="ml-1.5 text-[11.5px] font-semibold px-2 py-1 rounded border border-[#e7e9ee] text-[#0e1420] hover:bg-[#f5f6f8]"
                        >
                          Unapprove
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {data?.next_cursor && (
            <div data-testid="tyc-page-truncated-notice" className="px-4 py-3 border-t border-[#eef1f6] bg-[#fef3c7] text-[12px] text-[#854d0e]">
              <b>Showing first {cards.length} of a larger match set.</b> Bulk-select only affects visible rows.
              Narrow the batch dropdown or use the search box above to isolate a specific batch (e.g. <span className="font-mono-data">backfill-2026-08</span>) before selecting all.
            </div>
          )}
        </div>
      )}

      {detailCard && <CardDetailModal card={detailCard} onClose={() => setDetailCard(null)} />}
      {bulkResult && <BulkResultModal result={bulkResult} onClose={() => setBulkResult(null)} />}
      {cancelDialog && (
        <CancelReasonDialog
          count={cancelDialog.ids.length}
          code={cancelCode}
          otherText={cancelOtherText}
          onSelectCode={setCancelCode}
          onOtherTextChange={setCancelOtherText}
          onCancel={() => setCancelDialog(null)}
          onConfirm={submitCancel}
        />
      )}
    </div>
  );
}

// Cancel-reason picker (2026-08-12). 5 canned reasons + Other freeform.
// Confirm stays disabled until a code is selected, and until "Other" has
// non-blank text. Enter submits when the form is valid; Escape cancels.
function CancelReasonDialog({ count, code, otherText, onSelectCode, onOtherTextChange, onCancel, onConfirm }) {
  const isOther = code === "other";
  const valid = code && (!isOther || otherText.trim().length > 0);
  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-lg" data-testid="tyc-cancel-dialog">
        <DialogHeader>
          <DialogTitle>Cancel {count} card{count === 1 ? "" : "s"}?</DialogTitle>
          <DialogDescription>
            Pick a reason so the audit trail stays reportable. This can&apos;t be undone —
            re-ingesting a cancelled card requires <code className="font-mono-data text-[11px]">force=true</code>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          {CANCEL_REASONS.map((r) => {
            const active = code === r.code;
            return (
              <button
                key={r.code}
                type="button"
                onClick={() => onSelectCode(r.code)}
                data-testid={`tyc-cancel-reason-${r.code}`}
                data-active={active ? "true" : "false"}
                className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-colors ${
                  active
                    ? "border-[#0072bc] bg-[#e7f4fb]"
                    : "border-[#e7e9ee] hover:border-[#9ca3af] bg-white"
                }`}
              >
                <div className="font-semibold text-[14px] text-[#0e1420]">{r.label}</div>
                <div className="text-[12px] text-[color:var(--cc-ink-muted)] mt-0.5">{r.hint}</div>
              </button>
            );
          })}
          {isOther && (
            <div className="pt-2">
              <label className="text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-bold">
                Reason (required)
              </label>
              <Textarea
                autoFocus
                rows={3}
                maxLength={500}
                value={otherText}
                onChange={(e) => onOtherTextChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && valid) onConfirm();
                }}
                data-testid="tyc-cancel-reason-other-text"
                placeholder="Why is this card being cancelled?"
                className="mt-1"
              />
              <div className="text-[11px] text-[color:var(--cc-ink-muted)] mt-1">
                {otherText.length}/500 · ⌘/Ctrl-Enter to confirm
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel} data-testid="tyc-cancel-dialog-abort">
            Keep as-is
          </Button>
          <Button
            onClick={onConfirm}
            disabled={!valid}
            data-testid="tyc-cancel-dialog-confirm"
            className="bg-[#ef5757] hover:bg-[#d63e3e] text-white"
          >
            Cancel {count} card{count === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Shown after any bulk operation with a per-card breakdown of successes vs
// skips. Beats an alert() at scale — a 247-card batch with 3 already-cancelled
// cards would produce an alert string bigger than most screens.
function BulkResultModal({ result, onClose }) {
  const hasSkips = result.skipped && result.skipped.length > 0;
  const hasError = Boolean(result.error);
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="tyc-bulk-result-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl w-full max-w-[520px] shadow-xl border border-[#e7e9ee] my-8">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e7e9ee]">
          <div>
            <h2 className="font-display font-bold text-[17px] text-[#0e1420]">
              {result.action} — {hasError ? "failed" : "completed"}
            </h2>
            <p className="text-[11.5px] text-[color:var(--cc-ink-muted)] mt-0.5 font-mono-data">
              {result.requested} requested · {result.elapsedMs}ms round-trip
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-[#f5f6f8] rounded-md text-[#4b5563]">✕</button>
        </div>
        <div className="p-5 space-y-3 text-[13px]">
          {hasError ? (
            <div className="bg-[#fee2e2] border border-[#f87171] rounded-lg p-3 text-[#7a1c1c]">
              <div className="font-semibold mb-1">Server error</div>
              <div className="font-mono-data text-[11.5px]">{result.error}</div>
              <div className="text-[11.5px] mt-2">Zero cards were modified. Safe to retry.</div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[#dcfce7] border border-[#4ade80] rounded-lg p-3">
                <div className="text-[10.5px] uppercase tracking-widest text-[#166534] font-semibold">Succeeded</div>
                <div className="font-mono-data font-bold text-[24px] text-[#166534] leading-tight mt-1" data-testid="tyc-bulk-result-succeeded">
                  {result.acted.length}
                </div>
              </div>
              <div className={`border rounded-lg p-3 ${hasSkips ? "bg-[#fef3c7] border-[#facc15]" : "bg-[#f5f6f8] border-[#e7e9ee]"}`}>
                <div className={`text-[10.5px] uppercase tracking-widest font-semibold ${hasSkips ? "text-[#854d0e]" : "text-[color:var(--cc-ink-muted)]"}`}>Skipped</div>
                <div className={`font-mono-data font-bold text-[24px] leading-tight mt-1 ${hasSkips ? "text-[#854d0e]" : "text-[color:var(--cc-ink-muted)]"}`} data-testid="tyc-bulk-result-skipped">
                  {result.skipped.length}
                </div>
              </div>
            </div>
          )}
          {hasSkips && (
            <div className="border border-[#e7e9ee] rounded-lg overflow-hidden">
              <div className="bg-[#f5f6f8] px-3 py-2 text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">
                Reasons ({result.skipped.length})
              </div>
              <div className="max-h-[240px] overflow-y-auto">
                <table className="w-full text-[12px]">
                  <tbody>
                    {result.skipped.map((s) => (
                      <tr key={s.id} className="border-t border-[#eef1f6]">
                        <td className="px-3 py-2 font-mono-data text-[10.5px] text-[color:var(--cc-ink-muted)]">{String(s.id).slice(0, 8)}…</td>
                        <td className="px-3 py-2 text-[#0e1420]">{s.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-3 py-2 border-t border-[#eef1f6] text-[11px] text-[color:var(--cc-ink-muted)]">
                Succeeded cards were still committed — this is not a partial rollback. Refresh the list to see updated statuses.
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[#e7e9ee] bg-[#f9fafb] rounded-b-xl">
          <button type="button" onClick={onClose} data-testid="tyc-bulk-result-close" className="px-4 py-2 text-[12.5px] font-semibold rounded-lg bg-[#0072bc] text-white hover:bg-[#005a95]">
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

function AutoSendToggle({ enabled, onToggle, disabled, updatedAt, updatedBy }) {
  return (
    <div className="inline-flex items-center gap-3 bg-white border border-[#e7e9ee] rounded-lg px-3 py-2" data-testid="tyc-autosend-toggle">
      <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-bold">Auto-send</div>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        onClick={onToggle}
        disabled={disabled}
        data-testid="tyc-autosend-switch"
        data-enabled={enabled ? "true" : "false"}
        className={`relative w-11 h-6 rounded-full transition ${enabled ? "bg-[#0e8a68]" : "bg-[#cbd5e1]"} disabled:opacity-60`}
      >
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-5" : ""}`} />
      </button>
      <div className="text-[10.5px] font-mono-data text-[color:var(--cc-ink-muted)]">
        {enabled ? "ON" : "OFF"}
        {updatedBy && (
          <div className="text-[9.5px] mt-0.5">by {updatedBy.split("@")[0]} · {fmtDate(updatedAt)}</div>
        )}
      </div>
    </div>
  );
}

function CardDetailModal({ card, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="tyc-detail-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl w-full max-w-[640px] shadow-xl border border-[#e7e9ee] my-8">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e7e9ee]">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-display font-bold text-[18px] text-[#0e1420]">{card.customer_name}</h2>
              <StatusPill status={card.status} />
              <BatchBadge tag={card.batch_tag} />
            </div>
            <div className="text-[11px] font-mono-data text-[color:var(--cc-ink-muted)] mt-0.5">
              {card.external_id} · Job {card.job_number} · Completed {fmtDate(card.job_completed_at)}
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-[#f5f6f8] rounded-md text-[#4b5563]">✕</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4 text-[13px]">
            <div>
              <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">Tech</div>
              <div className="mt-0.5">{card.tech_name}</div>
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">Revenue</div>
              <div className="mt-0.5 font-mono-data font-bold text-[#0072bc]">{fmt$(card.job_revenue)}</div>
            </div>
            <div className="col-span-2">
              <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">Mailing address</div>
              {card.customer_address?.line1 || card.customer_address?.city ? (
                <div className="mt-0.5" data-testid="tyc-detail-address">
                  {card.customer_address?.line1 && (
                    <>
                      {card.customer_address.line1}
                      {card.customer_address?.line2 && <>, {card.customer_address.line2}</>}
                      <br />
                    </>
                  )}
                  {[card.customer_address?.city, card.customer_address?.state, card.customer_address?.postal_code]
                    .filter(Boolean).join(card.customer_address?.state ? ", " : " ")
                    .replace(/,\s*$/, "")}
                  {card.customer_address?.country && (
                    <>
                      <br />
                      <span className="text-[11px] text-[color:var(--cc-ink-muted)]">{card.customer_address.country}</span>
                    </>
                  )}
                </div>
              ) : (
                <div
                  className="mt-0.5 text-[12px] italic text-[color:var(--cc-ink-muted)]"
                  data-testid="tyc-detail-address-missing"
                >
                  Address unavailable — this often causes the send to fail before dispatch.
                </div>
              )}
            </div>
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">Merged message</div>
            <div
              className="mt-1 bg-[#faf8f4] border border-[#e7d9b5] rounded-lg p-4 text-[13.5px] leading-relaxed whitespace-pre-wrap font-serif text-[#3d3423]"
              data-testid="tyc-detail-message"
            >
              {card.message}
            </div>
          </div>
          {card.status === "APPROVED" && card.approved_by && (
            <div className="text-[11.5px] text-[color:var(--cc-ink-muted)]">
              Approved by <b>{card.approved_by}</b> · {fmtDate(card.approved_at)}
              {card.auto_approved && <span className="ml-2 text-[10px] font-mono-data uppercase tracking-widest text-[#0072bc]">auto</span>}
              {card.approval_note && <div className="mt-1 italic">&ldquo;{card.approval_note}&rdquo;</div>}
            </div>
          )}
          {card.status === "SENT" && (
            <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] flex items-center gap-2">
              <Send size={12} strokeWidth={1.8} />
              Sent {fmtDate(card.sent_at)} · Handwrytten order <span className="font-mono-data">{card.handwrytten_order_id}</span>
              {card.test_mode && <span className="text-[10px] font-mono-data uppercase tracking-widest text-[#854d0e]">test mode</span>}
            </div>
          )}
          {card.status === "FAILED" && (
            <div className="text-[12px] bg-[#fee2e2] border border-[#f87171] rounded-lg p-3 text-[#7a1c1c]">
              <div className="font-semibold mb-1">Last send error</div>
              <div className="font-mono-data text-[11px]">{card.last_send_error || "—"}</div>
            </div>
          )}
          {card.status === "CANCELLED" && (
            <div className="text-[12px] text-[color:var(--cc-ink-muted)]">
              Cancelled by <b>{card.cancelled_by}</b> · {fmtDate(card.cancelled_at)}
              {card.cancel_reason_code && (
                <span
                  className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-[#e5e7eb] text-[#374151] text-[10px] font-mono-data uppercase tracking-widest border border-[#9ca3af]"
                  data-testid={`tyc-cancel-code-chip-${card.id}`}
                >
                  {CANCEL_REASON_LABEL[card.cancel_reason_code] || card.cancel_reason_code}
                </span>
              )}
              {card.cancel_reason && <div className="mt-0.5 italic">&ldquo;{card.cancel_reason}&rdquo;</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
